/** @jsxImportSource @emotion/react */

import React, { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';

import updateContactsStyles from './update-contacts.styles';
import { useMediaQuery } from '@mui/material';
import PredefinedSwipableDrawer from '../swipable-edge-drawer/swipable-drawer.component';
import { useIntl } from 'react-intl';
import { Z_INDEX } from '../../constants/zIndex';
import { useUpdateTransactionContactsMutation } from '../../api/transactions/transactions.api';
import {
    ContactDetailsForm,
    ContactDetailsFormState,
} from '../contact-details-form/contact-details-form.component';

type PropTypes = {
    transactionId: string;
    phone?: string;
    email?: string;
    isOpen: boolean;
    onClose: () => void;
    onOpen: () => void;
};

export const UpdateContacts = (props: PropTypes) => {
    const { phone = '', email = '', isOpen, onClose, onOpen, transactionId } = props;
    const isDesktop = useMediaQuery('(min-width:600px)');
    const intl = useIntl();
    const [updateTransactionContacts, { isLoading: isUpdatingContacts }] =
        useUpdateTransactionContactsMutation();

    const [formState, setFormState] = useState<ContactDetailsFormState>(() => ({
        phone,
        email,
        isInvalid: { phone: false, email: false },
    }));

    useEffect(() => {
        setFormState({
            phone,
            email,
            isInvalid: { phone: false, email: false },
        });
    }, [phone, email]);

    const handleUpdateContacts = useCallback(async () => {
        if (
            !formState.phone ||
            formState.phone.includes('_') ||
            formState.isInvalid.phone ||
            formState.isInvalid.email
        ) {
            return;
        }
        try {
            await updateTransactionContacts({
                transactionId,
                phone: formState.phone,
                ...(formState.email && { email: formState.email }),
            }).unwrap();
            onClose();
        } catch {
            // Error handling can be added (e.g. toast)
        }
    }, [formState, transactionId, updateTransactionContacts, onClose]);

    const isSaveDisabled =
        !formState.phone ||
        formState.phone.includes('_') ||
        formState.isInvalid.phone ||
        formState.isInvalid.email;

    const content = (
        <div style={{ width: '100%' }} css={updateContactsStyles.contentWrapper}>
            <div css={updateContactsStyles.menuHeader}>
                {intl.formatMessage({ id: 'updateContactDetails' })}
            </div>
            <div css={updateContactsStyles.menuDescription}>
                {intl.formatMessage({ id: 'updateContactDetailsDescription' })}
            </div>
            <ContactDetailsForm
                defaultPhone={phone}
                defaultEmail={email}
                onFormChange={setFormState}
                showPhoneDescription={false}
                showEmailDescription={false}
                requiredPhone={true}
            />
            <div css={updateContactsStyles.actionWrapper}>
                <Button
                    variant="text"
                    sx={{
                        textTransform: 'capitalize',
                        boxShadow: 'none',
                        color: '#004FC6',
                        fontSize: '16px',
                        fontWeight: 700,
                        width: '100%',
                        padding: '14px',
                        borderRadius: '10px',
                        lineHeight: '120%',
                    }}
                    disabled={isSaveDisabled}
                    onClick={onClose}
                    loading={isUpdatingContacts}>
                    {intl.formatMessage({ id: 'cancel' })}
                </Button>

                <Button
                    variant="contained"
                    sx={{
                        backgroundColor: '#004FC6',
                        textTransform: 'capitalize',
                        fontSize: '16px',
                        fontWeight: 700,
                        width: '100%',
                        padding: '14px',
                        borderRadius: '10px',
                        lineHeight: '120%',
                    }}
                    onClick={handleUpdateContacts}
                    loading={isUpdatingContacts}
                    disabled={isSaveDisabled}>
                    {intl.formatMessage({ id: 'save' })}
                </Button>
            </div>
        </div>
    );

    return (
        <>
            {isDesktop ? (
                <>
                    {isOpen && (
                        <div css={updateContactsStyles.menuDesktopBackdrop}>
                            <div css={updateContactsStyles.menuDesktopContent}>
                                <div
                                    css={[
                                        updateContactsStyles.menuList,
                                        updateContactsStyles.menuListDesktop,
                                    ]}>
                                    {content}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <PredefinedSwipableDrawer
                    onOpen={onOpen}
                    onClose={onClose}
                    isOpen={isOpen}
                    snapPoints={[0.5]}
                    zIndex={Z_INDEX.DRAWER_LEGEND}>
                    <div css={[updateContactsStyles.menuList]}>{content}</div>
                </PredefinedSwipableDrawer>
            )}
        </>
    );
};
